import { H3 } from "@/shared/components/typography";
import t from "@/lang/lang";
import {
  DataTable,
  DataTableContainer,
  DataTableFilters,
  useDataTableController,
} from "@/shared/components/tables/data-table";
import { useHtzAirHandlingUnitDataListQuery } from "@/services/backend/htz/ahu/api/ahu-data-api";
import { ColumnDef } from "@tanstack/react-table";
import { AirHandlingUnitData } from "@/services/backend/htz/ahu/types/air-handling-unit-data";
import { NavLink, Outlet, useParams } from "react-router";
import { Button } from "@/shared/components/ui/button";
import { Search } from "lucide-react";
import { NewAhuDataDialog } from "@/routes/customers/[customerId]/ahu-units/_components/new-ahu-dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  CustomerPageMain,
  CustomerPageMainContent,
  CustomerPageMainHeader,
} from "@/routes/customers/[customerId]/_components/customer-page";
import { CustomerPageBreadcrumbs } from "@/routes/customers/[customerId]/_components/customer-page-breadcrumbs";
import { UUID } from "@bitory-gmbh/nidavellir";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

export function AhuUnitsRoute() {
  return <Outlet />;
}

export function AhuUnitsIndexRoute() {
  const { customerId } = useParams();
  const { companyId } = useActiveCompany();

  return (
    <CustomerPageMain>
      <CustomerPageMainHeader>
        <CustomerPageBreadcrumbs
          breadcrumbs={[
            {
              to: `/customers/${customerId}/ahu-units`,
              label: t("RLT-Anlagen"),
            },
          ]}
        />
      </CustomerPageMainHeader>
      <CustomerPageMainContent>
        <RltUnitsTable customerId={customerId!} companyId={companyId} />
      </CustomerPageMainContent>
    </CustomerPageMain>
  );
}

function RltUnitsTable({
  customerId,
  companyId,
}: {
  customerId: UUID;
  companyId: UUID;
}) {
  const {
    data: list,
    isLoading,
    error,
  } = useHtzAirHandlingUnitDataListQuery({
    customer: {
      active: true,
      values: [customerId],
    },
  });

  const airHandlingUnits = list?.items ?? [];
  const table = useDataTableController(tableColumns, airHandlingUnits);

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <DataTableContainer className="animate-pulse">
        <DataTableFilters className="flex justify-between space-x-2">
          <H3>{t("RLT-Anlagen")}</H3>
          <NewAhuDataDialog customerId={customerId} companyId={companyId} />
        </DataTableFilters>
        <DataTable columns={tableColumns} table={table} />
      </DataTableContainer>
    );
  }

  return (
    <DataTableContainer>
      <DataTableFilters className="flex justify-between space-x-2">
        <H3>{t("RLT-Anlagen")}</H3>
        <NewAhuDataDialog customerId={customerId} companyId={companyId} />
      </DataTableFilters>
      <DataTable columns={tableColumns} table={table} />
    </DataTableContainer>
  );
}

const tableColumns: ColumnDef<AirHandlingUnitData>[] = [
  {
    header: t("Name"),
    accessorKey: "name",
  },
  {
    id: "actions",
    header: t("Aktionen"),
    cell: ({ row }) => {
      const ahu = row.original;

      return (
        <NavLink to={`${ahu.id}`}>
          <Button variant="ghost" size="sm" className="-mt-1.5 -mb-2.5">
            <Search className="h-4 w-4" />
          </Button>
        </NavLink>
      );
    },
  },
];
